import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addDays } from "date-fns";
import { api } from "../lib/api";
import { ymd } from "../lib/date";
import type { Department, Employee, Shift, ShiftBidRow, User } from "../lib/types";
import { useEffectiveUser } from "../lib/auth";
import { can } from "../lib/permissions";

// Shift Bid — 2-week Sun-aligned bid sheet per (department, start
// Sunday). L2+ builds rows and marks XXX days off; officers in the
// dept + matching home shift pick a row in seniority order. A more
// senior officer can bump a less-senior claimant.

export function ShiftBid() {
  const me = useEffectiveUser();
  const canEdit = can.editRoster(me);
  const qc = useQueryClient();

  function sundayOf(d: Date): Date {
    const c = new Date(d); c.setHours(0, 0, 0, 0);
    c.setDate(c.getDate() - c.getDay());
    return c;
  }
  const [anchor, setAnchor] = useState<Date>(() => sundayOf(new Date()));
  const days = useMemo(
    () => Array.from({ length: 14 }, (_, i) => addDays(anchor, i)),
    [anchor],
  );
  const startStr = ymd(anchor);

  const departments = useQuery({ queryKey: ["departments"], queryFn: api.listDepartments });
  const shifts = useQuery({ queryKey: ["shifts"], queryFn: () => api.listShifts() });
  const employees = useQuery({ queryKey: ["employees"], queryFn: () => api.listEmployees() });
  // Users query maps employee_id → user_id so a bump can ping the
  // displaced officer's account. Anyone in the agency can read;
  // the notifyUser fan-out below silent-drops when there's no
  // linked account.
  const users = useQuery({ queryKey: ["users"], queryFn: api.listUsers });
  const sortedDepts = useMemo(
    () => [...(departments.data ?? [])].sort(
      (a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999)
        || a.name.localeCompare(b.name),
    ),
    [departments.data],
  );
  const [selectedDeptId, setSelectedDeptId] = useState<string | null>(null);
  const activeDept: Department | null = useMemo(() => {
    if (selectedDeptId) return sortedDepts.find((d) => d.id === selectedDeptId) ?? null;
    return sortedDepts[0] ?? null;
  }, [selectedDeptId, sortedDepts]);
  const shiftsForActiveDept = useMemo(() => {
    if (!activeDept) return [];
    return (shifts.data ?? [])
      .filter((s) => s.departmentId === activeDept.id && s.inRotation !== false)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  }, [shifts.data, activeDept]);

  const [editMode, setEditMode] = useState(false);

  // Sheet lookup + rows.
  const sheetQ = useQuery({
    queryKey: ["shiftBidSheet", activeDept?.id, startStr],
    queryFn: () => activeDept
      ? api.getShiftBidSheet(activeDept.id, startStr)
      : Promise.resolve(null),
    enabled: !!activeDept,
  });
  const sheetId = sheetQ.data?.id ?? null;
  // Locked sheets reject every mutation server-side (rows trigger
  // + claim/release RPC guards). Force edit mode off so the UI
  // doesn't tempt supervisors with cells they can't toggle.
  const isLocked = !!sheetQ.data?.lockedAt;
  const isOpen = !!sheetQ.data?.openedAt && !isLocked;
  const inEdit = canEdit && editMode && !isLocked;
  const rowsQ = useQuery({
    queryKey: ["shiftBidRows", sheetId],
    queryFn: () => sheetId ? api.listShiftBidRows(sheetId) : Promise.resolve([]),
    enabled: !!sheetId,
  });

  const [error, setError] = useState<string | null>(null);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["shiftBidRows", sheetId] });
    qc.invalidateQueries({ queryKey: ["shiftBidSheet", activeDept?.id, startStr] });
    // Also refresh the top-of-page "live" banner state so a claim /
    // release / bump immediately shows or hides it without waiting
    // for the window-focus refetch to kick in.
    qc.invalidateQueries({ queryKey: ["myShiftBidClaims"] });
    qc.invalidateQueries({ queryKey: ["openShiftBidSheets"] });
  };

  const upsertSheet = useMutation({
    mutationFn: () => activeDept
      ? api.upsertShiftBidSheet(activeDept.id, startStr)
      : Promise.reject(new Error("No department")),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shiftBidSheet", activeDept?.id, startStr] });
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Save failed."),
  });
  const addRowMut = useMutation({
    mutationFn: (input: { sheetId: string; shiftId: string | null; sortOrder: number }) =>
      api.addShiftBidRow(input),
    onSuccess: invalidate,
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Add failed."),
  });
  const updateRowMut = useMutation({
    mutationFn: (input: { id: string; offDates: string[] }) =>
      api.updateShiftBidRow(input.id, { offDates: input.offDates }),
    onSuccess: invalidate,
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Save failed."),
  });
  const deleteRowMut = useMutation({
    mutationFn: (id: string) => api.deleteShiftBidRow(id),
    onSuccess: invalidate,
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Delete failed."),
  });
  const claimMut = useMutation({
    // Track the previous claimant before mutating so we can ping
    // them after the RPC returns success. The row's current
    // claimed_by_employee_id comes from the rowsQ cache.
    mutationFn: async (rowId: string) => {
      const before = (rowsQ.data ?? []).find((r) => r.id === rowId);
      const bumped = before?.claimedByEmployeeId ?? null;
      const row = await api.claimShiftBidRow(rowId);
      // Notify the displaced officer, if any and if they have a
      // linked auth account. Silent-drop otherwise.
      if (bumped && bumped !== row.claimedByEmployeeId) {
        const bumpedUser = (users.data ?? []).find(
          (u: User) => u.employeeId === bumped,
        );
        if (bumpedUser) {
          void api.notifyUser(
            bumpedUser.id,
            "Shift Bid row taken",
            "A more senior officer has claimed the shift-bid row you were holding.\n"
              + "Open Shift Bid to pick a different row.",
          );
        }
      }
      return row;
    },
    onSuccess: invalidate,
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Claim failed."),
  });
  const releaseMut = useMutation({
    mutationFn: (rowId: string) => api.releaseShiftBidRow(rowId),
    onSuccess: invalidate,
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Release failed."),
  });
  const lockMut = useMutation({
    mutationFn: (locked: boolean) => sheetId
      ? api.setShiftBidSheetLocked(sheetId, locked)
      : Promise.reject(new Error("Save the sheet first")),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shiftBidSheet", activeDept?.id, startStr] });
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Lock failed."),
  });
  const openMut = useMutation({
    mutationFn: (open: boolean) => sheetId
      ? api.setShiftBidSheetOpen(sheetId, open)
      : Promise.reject(new Error("Save the sheet first")),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shiftBidSheet", activeDept?.id, startStr] });
      qc.invalidateQueries({ queryKey: ["openShiftBidSheets"] });
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Open failed."),
  });
  const [flash, setFlash] = useState<string | null>(null);
  const [applyProgress, setApplyProgress] = useState<string | null>(null);
  // Apply: for each CLAIMED row on the current sheet, translate the
  // row's off_dates into a (week1, week2) days-off pattern anchored
  // to the sheet's start_date, migrate the officer's shift if the
  // row targets a different one, patch the employee record, and
  // materialize XXX assignment rows via applyDaysOffPattern. Mirrors
  // the Settings → Shift Bid bulk-save flow so the same downstream
  // invalidations light both surfaces up.
  const applyMut = useMutation({
    mutationFn: async () => {
      if (!sheetQ.data) throw new Error("No sheet loaded.");
      const sheet = sheetQ.data;
      const startDateObj = new Date(sheet.startDate + "T00:00:00");
      const msDay = 86_400_000;
      const claimed = (rowsQ.data ?? [])
        .filter((r) => r.claimedByEmployeeId);
      if (claimed.length === 0) throw new Error("No claimed rows to apply.");
      let done = 0;
      for (const row of claimed) {
        const empId = row.claimedByEmployeeId!;
        const emp = (employees.data ?? []).find((e) => e.id === empId);
        if (!emp) continue;
        // Split off-dates by which week of the 14-day window they
        // land in. Anything outside 0..13 is ignored.
        const w1 = new Set<number>();
        const w2 = new Set<number>();
        for (const dateStr of row.offDates) {
          const d = new Date(dateStr + "T00:00:00");
          const offset = Math.round((d.getTime() - startDateObj.getTime()) / msDay);
          if (offset < 0 || offset >= 14) continue;
          const wd = d.getDay();
          if (offset < 7) w1.add(wd); else w2.add(wd);
        }
        const w1Arr = Array.from(w1).sort((a, b) => a - b);
        const w2Arr = Array.from(w2).sort((a, b) => a - b);
        const w2SameAsW1 = w1Arr.length === w2Arr.length
          && w1Arr.every((v, i) => v === w2Arr[i]);
        const finalW2 = w2SameAsW1 ? null : w2Arr;

        const oldShiftId = emp.homeShiftId;
        const newShiftId = row.shiftId ?? oldShiftId;
        const shiftChanged = (newShiftId ?? "") !== (oldShiftId ?? "");
        if (shiftChanged && oldShiftId && newShiftId) {
          await api.moveOfficerToShift({
            employeeId: empId,
            oldShiftId,
            newShiftId,
            startDate: sheet.startDate,
            silent: true,
          });
        }
        const patch: Record<string, unknown> = {
          daysOffWeekdays: w1Arr,
          daysOffWeekdaysW2: finalW2,
          daysOffStartDate: w1Arr.length > 0 ? sheet.startDate : null,
        };
        if (shiftChanged) patch.homeShiftId = newShiftId;
        await api.updateEmployee(empId, patch);
        if (w1Arr.length > 0 && newShiftId) {
          await api.applyDaysOffPattern({
            employeeId: empId,
            week1: w1Arr,
            week2: finalW2,
            startDate: sheet.startDate,
            homeShiftId: newShiftId,
            silent: true,
          });
        }
        done += 1;
        setApplyProgress(`Applied ${done} of ${claimed.length}…`);
      }
      setApplyProgress(null);
      return done;
    },
    onSuccess: (count) => {
      // Invalidate everything that reads employee patterns or
      // schedule rows so the Settings → Shift Bid form (which
      // seeds from employees.data), the Schedule grid, and the
      // Records report all reload against the fresh state.
      qc.invalidateQueries({ queryKey: ["employees"] });
      qc.invalidateQueries({ queryKey: ["assignments"] });
      qc.invalidateQueries({ queryKey: ["coverage"] });
      setError(null);
      setFlash(`Applied to ${count} officer${count === 1 ? "" : "s"}. Settings → Shift Bid now reflects the new patterns.`);
    },
    onError: (e: unknown) => {
      setApplyProgress(null);
      setError(e instanceof Error ? e.message : "Apply failed.");
    },
  });

  // When edit mode flips on and no sheet exists yet, materialize one
  // so the row-add works immediately. Silent no-op if it already
  // exists (upsert on conflict).
  useEffect(() => {
    if (!canEdit || !editMode || !activeDept) return;
    if (sheetQ.isLoading) return;
    if (sheetQ.data) return;
    upsertSheet.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, editMode, activeDept?.id, sheetQ.data, sheetQ.isLoading]);

  const empById = useMemo(
    () => new Map((employees.data ?? []).map((e) => [e.id, e])),
    [employees.data],
  );
  const myEmp = useMemo(
    () => me?.employeeId ? empById.get(me.employeeId) ?? null : null,
    [empById, me?.employeeId],
  );
  const rowsByShift = useMemo(() => {
    const m = new Map<string, ShiftBidRow[]>();
    for (const r of rowsQ.data ?? []) {
      const key = r.shiftId ?? "__unassigned__";
      const list = m.get(key) ?? [];
      list.push(r);
      m.set(key, list);
    }
    return m;
  }, [rowsQ.data]);

  // Per-row eligibility for the signed-in officer. Returned string
  // drives both the trailing action chip and the row background:
  //   own      — already claimed by me (Release button, sky highlight)
  //   claim    — unclaimed and I match the sheet's dept (Claim button)
  //   bump     — held by an officer of lower seniority I can bump
  //   ineligible — wrong dept (no button, dim)
  //   held     — held by equal-or-senior officer (no button)
  //   no_employee — my account isn't linked (no button)
  //
  // Cross-shift bidding: any officer in the department can claim
  // any row on the sheet, regardless of what shift the row is
  // tied to. The home_shift_id update happens at Apply time, not
  // Claim time — so the schedule only shifts once an L1 confirms
  // the bid.
  function eligibilityFor(row: ShiftBidRow): {
    kind: "own" | "claim" | "bump" | "ineligible" | "held" | "no_employee";
  } {
    if (!myEmp) return { kind: "no_employee" };
    if (row.claimedByEmployeeId === myEmp.id) return { kind: "own" };
    if (!activeDept) return { kind: "ineligible" };
    if (myEmp.departmentId !== activeDept.id) return { kind: "ineligible" };
    if (!row.claimedByEmployeeId) return { kind: "claim" };
    const current = empById.get(row.claimedByEmployeeId);
    if (!current) return { kind: "claim" };
    // Bump only if my hire_date is STRICTLY earlier than the
    // current claimant's. Nulls sort last.
    const mine = myEmp.hireDate;
    const theirs = current.hireDate;
    if (!mine) return { kind: "held" };
    if (theirs && mine < theirs) return { kind: "bump" };
    if (!theirs) return { kind: "bump" };
    return { kind: "held" };
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Shift Bid</h1>
          <p className="text-sm text-slate-500">
            Two-week bid sheet (Sun → Sat). L2+ builds rows and marks
            days off; officers pick a row — a more senior officer can
            bump a less-senior one. Beta.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {canEdit && (
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Department
              </label>
              <select
                className="rounded border-slate-300 text-sm px-2 py-1"
                value={activeDept?.id ?? ""}
                onChange={(e) => setSelectedDeptId(e.target.value)}
              >
                {sortedDepts.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
              Start (Sunday)
            </label>
            <input
              type="date"
              value={startStr}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                const [y, m, dd] = v.split("-").map(Number);
                if (!y || !m || !dd) return;
                setAnchor(sundayOf(new Date(y, m - 1, dd)));
              }}
              className="rounded border-slate-300 text-sm px-2 py-1"
              title="Pick any date — snaps to that week's Sunday"
            />
          </div>
          {canEdit && (
            <label className={
              "flex items-center gap-2 text-sm select-none "
              + (isLocked
                ? "text-slate-400 dark:text-slate-500"
                : "text-slate-700 dark:text-slate-200")
            }>
              <input
                type="checkbox"
                checked={editMode && !isLocked}
                onChange={(e) => setEditMode(e.target.checked)}
                disabled={isLocked}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50"
              />
              <span>Edit</span>
            </label>
          )}
          {canEdit && sheetId && !isLocked && (
            <button
              type="button"
              onClick={() => openMut.mutate(!isOpen)}
              disabled={openMut.isPending}
              className={
                "text-xs font-semibold uppercase tracking-wider px-2 py-1 rounded border disabled:opacity-50 "
                + (isOpen
                  ? "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 "
                    + "dark:bg-slate-700 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-600"
                  : "bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100 "
                    + "dark:bg-emerald-900/40 dark:border-emerald-700 dark:text-emerald-200 dark:hover:bg-emerald-900/60")
              }
              title={isOpen
                ? "Close bidding — the top-of-page banner stops nudging officers who haven't claimed"
                : "Open bidding — eligible officers who haven't claimed will see a banner at the top of every page"}
            >
              {isOpen ? "Close bidding" : "Open for bidding"}
            </button>
          )}
          {isOpen && (
            <span
              className="text-[10px] font-semibold uppercase tracking-wider text-emerald-800 bg-emerald-100 border border-emerald-300 rounded px-1.5 py-0.5 dark:bg-emerald-900/40 dark:border-emerald-700 dark:text-emerald-200"
              title={sheetQ.data?.openedAt
                ? `Live since ${new Date(sheetQ.data.openedAt).toLocaleString()}`
                : "Live"}
            >
              Live
            </span>
          )}
          {canEdit && sheetId && (
            <button
              type="button"
              onClick={() => lockMut.mutate(!isLocked)}
              disabled={lockMut.isPending}
              className={
                "text-xs font-semibold uppercase tracking-wider px-2 py-1 rounded border disabled:opacity-50 "
                + (isLocked
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100 "
                    + "dark:bg-emerald-900/40 dark:border-emerald-700 dark:text-emerald-200 dark:hover:bg-emerald-900/60"
                  : "bg-rose-50 border-rose-200 text-rose-800 hover:bg-rose-100 "
                    + "dark:bg-rose-900/40 dark:border-rose-700 dark:text-rose-200 dark:hover:bg-rose-900/60")
              }
              title={isLocked
                ? "Unlock this sheet so rows can be edited / claimed again"
                : "Lock this sheet — no more claims, releases, or row edits until it's unlocked"}
            >
              {isLocked ? "Unlock" : "Lock"}
            </button>
          )}
          {isLocked && (
            <span
              className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-100 border border-slate-300 rounded px-1.5 py-0.5 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-300"
              title={sheetQ.data?.lockedAt
                ? `Locked ${new Date(sheetQ.data.lockedAt).toLocaleString()}`
                : "Sheet locked"}
            >
              Locked
            </span>
          )}
          {canEdit && isLocked && (
            <button
              type="button"
              onClick={() => {
                const n = (rowsQ.data ?? []).filter((r) => r.claimedByEmployeeId).length;
                if (n === 0) {
                  setError("No claimed rows to apply.");
                  return;
                }
                if (!window.confirm(
                  `Apply ${n} claimed row${n === 1 ? "" : "s"} to the schedule?\n\n`
                    + "For each claimed row this will update the officer's days-off "
                    + "pattern (both weeks), move them to the row's shift if it differs "
                    + "from their current home, set the effective start date to "
                    + `${sheetQ.data?.startDate}, and materialize XXX rows across the `
                    + "next year. Manual edits already on the schedule are preserved.",
                )) return;
                applyMut.mutate();
              }}
              disabled={applyMut.isPending}
              className="text-xs font-semibold uppercase tracking-wider px-2 py-1 rounded border bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 border-brand-700"
              title="Push every claimed row's off-day pattern to the schedule (updates employee records + XXX rows)"
            >
              {applyMut.isPending ? "Applying…" : "Apply to schedule"}
            </button>
          )}
        </div>
      </header>

      {error && (
        <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2 flex items-start gap-2 dark:bg-rose-900/30 dark:border-rose-800 dark:text-rose-200">
          <span className="flex-1">{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-rose-400 hover:text-rose-700 font-semibold leading-none dark:text-rose-300 dark:hover:text-rose-100"
            aria-label="Dismiss"
          >×</button>
        </div>
      )}
      {applyProgress && (
        <div className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded px-3 py-2 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
          {applyProgress}
        </div>
      )}
      {flash && (
        <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2 flex items-start gap-2 dark:bg-emerald-900/30 dark:border-emerald-800 dark:text-emerald-200">
          <span className="flex-1">{flash}</span>
          <button
            type="button"
            onClick={() => setFlash(null)}
            className="text-emerald-400 hover:text-emerald-700 font-semibold leading-none dark:text-emerald-300 dark:hover:text-emerald-100"
            aria-label="Dismiss"
          >×</button>
        </div>
      )}

      {!activeDept ? (
        <div className="text-sm text-slate-500">
          No departments configured yet.
        </div>
      ) : (
        <div className="space-y-4">
          {(() => {
            // Cross-shift bidding (0062): officers in this dept can
            // claim ANY row on the sheet, so "eligible" and
            // "unclaimed" are dept-wide — the same set shows under
            // each shift block. "Claimed" is now across ALL rows on
            // the sheet, not just this block.
            const allRows = (rowsQ.data ?? []);
            const dept = activeDept;
            const sheetClaimedIds = new Set(
              allRows.map((r) => r.claimedByEmployeeId).filter(Boolean) as string[],
            );
            const deptEligible = (employees.data ?? [])
              .filter((e) => e.status !== "inactive" && !e.deletedAt)
              .filter((e) => dept && e.departmentId === dept.id);
            const unclaimed = deptEligible.filter((e) => !sheetClaimedIds.has(e.id));
            return (shiftsForActiveDept.length > 0
              ? shiftsForActiveDept.map((s) => ({ shift: s, key: s.id }))
              : [{ shift: null as Shift | null, key: "__unassigned__" }]
            ).map(({ shift, key }) => {
            const blockRows = rowsByShift.get(key) ?? [];
            void deptEligible; // dept-wide, kept available for future per-block tweaks
            const unclaimedOfficers = unclaimed;
            return (
              <ShiftSheet
                key={key}
                shift={shift}
                days={days}
                rows={blockRows}
                inEdit={inEdit}
                canEdit={canEdit}
                locked={isLocked}
                me={me?.employeeId ?? null}
                empById={empById}
                eligibilityFor={eligibilityFor}
                unclaimedOfficers={unclaimedOfficers}
                onNotifyUnclaimed={() => {
                  const targets = unclaimedOfficers
                    .map((e) => (users.data ?? []).find((u: User) => u.employeeId === e.id))
                    .filter((u): u is User => !!u);
                  if (targets.length === 0) {
                    setError("No eligible officers have linked accounts to notify.");
                    return;
                  }
                  for (const u of targets) {
                    void api.notifyUser(
                      u.id,
                      "Shift Bid open",
                      "A shift-bid sheet is open for your shift.\n"
                        + "Open the Shift Bid page and pick a row.",
                    );
                  }
                  setError(null);
                }}
                onAddRow={() => {
                  if (!sheetId) {
                    upsertSheet.mutate(undefined, {
                      onSuccess: (sheet) => addRowMut.mutate({
                        sheetId: sheet.id,
                        shiftId: shift?.id ?? null,
                        sortOrder: (rowsByShift.get(key)?.length ?? 0),
                      }),
                    });
                  } else {
                    addRowMut.mutate({
                      sheetId, shiftId: shift?.id ?? null,
                      sortOrder: (rowsByShift.get(key)?.length ?? 0),
                    });
                  }
                }}
                onToggleOff={(rowId, date, currentOff) => {
                  const next = new Set(currentOff);
                  if (next.has(date)) next.delete(date); else next.add(date);
                  updateRowMut.mutate({ id: rowId, offDates: [...next] });
                }}
                onRemoveRow={(rowId) => deleteRowMut.mutate(rowId)}
                onClaim={(rowId) => claimMut.mutate(rowId)}
                onRelease={(rowId) => releaseMut.mutate(rowId)}
                claimPending={claimMut.isPending || releaseMut.isPending}
              />
            );
          });
          })()}
        </div>
      )}
    </div>
  );
}

function ShiftSheet({
  shift, days, rows, inEdit, canEdit, locked, me, empById, eligibilityFor,
  unclaimedOfficers, onNotifyUnclaimed,
  onAddRow, onToggleOff, onRemoveRow, onClaim, onRelease, claimPending,
}: {
  shift: Shift | null;
  days: Date[];
  rows: ShiftBidRow[];
  inEdit: boolean;
  canEdit: boolean;
  // Sheet-level lock. When true every action button on rows is
  // hidden — the sheet is read-only until a supervisor unlocks
  // it via the header control.
  locked: boolean;
  me: string | null;
  empById: Map<string, Employee>;
  eligibilityFor: (row: ShiftBidRow) => {
    kind: "own" | "claim" | "bump" | "ineligible" | "held" | "no_employee";
  };
  unclaimedOfficers: Employee[];
  onNotifyUnclaimed: () => void;
  onAddRow: () => void;
  onToggleOff: (rowId: string, date: string, currentOff: string[]) => void;
  onRemoveRow: (rowId: string) => void;
  onClaim: (rowId: string) => void;
  onRelease: (rowId: string) => void;
  claimPending: boolean;
}) {
  return (
    <div className="flex justify-center">
      <section className="bg-white border border-slate-200 rounded-lg overflow-hidden inline-block">
        <header
          className="themed-fill px-3 py-1 text-[11px] font-semibold uppercase tracking-wider"
          style={{
            backgroundColor: shift?.color ?? "#e2e8f0",
            color: "#0f172a",
            ["--themed-color" as string]: shift?.color ?? "#e2e8f0",
          }}
        >
          {shift?.name?.trim() ?? "Unassigned"}
          {shift && (
            <span className="ml-2 font-normal normal-case tracking-normal opacity-80">
              · {shift.startTime}
            </span>
          )}
        </header>
        <div className="overflow-x-auto">
          <table className="text-xs" style={{ width: "auto" }}>
            <thead>
              <tr className="border-b border-slate-200 text-slate-500">
                <th className="text-left px-2 py-1 font-medium sticky left-0 bg-white w-[180px]">
                  Officer
                </th>
                {days.map((d) => (
                  <th
                    key={d.toISOString()}
                    className={
                      "text-center px-0 py-1 font-medium tabular-nums w-[38px] "
                      + (d.getDay() === 0 || d.getDay() === 6 ? "bg-slate-50" : "")
                    }
                    title={ymd(d)}
                  >
                    <div className="text-[9px] uppercase tracking-wider text-slate-400">
                      {d.toLocaleDateString(undefined, { weekday: "narrow" })}
                    </div>
                    <div>{d.getDate()}</div>
                  </th>
                ))}
                <th className="px-2 py-1 w-[80px]"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={1 + days.length + 1}
                    className="px-3 py-4 text-center text-slate-400 italic"
                  >
                    {inEdit
                      ? "No rows yet. Use \"+ Add row\" below."
                      : "No bid rows yet. Ask a supervisor to build the sheet."}
                  </td>
                </tr>
              ) : rows.map((r) => {
                const claimant = r.claimedByEmployeeId
                  ? empById.get(r.claimedByEmployeeId) ?? null
                  : null;
                const claimedByMe = !!me && r.claimedByEmployeeId === me;
                const off = new Set(r.offDates);
                const elig = eligibilityFor(r);
                // Row background: sky-50 when it's mine, amber-50/40
                // when I can act on it (claim or bump), otherwise
                // default. Draws the eye to actionable rows without
                // dominating the sheet.
                const rowBg = claimedByMe
                  ? "bg-sky-50 "
                  : (!inEdit && (elig.kind === "claim" || elig.kind === "bump"))
                    ? "bg-amber-50/50 "
                    : "";
                // Make the name slot double as the action target
                // when the signed-in officer can act on this row
                // (claim an open row, bump a less-senior claimant,
                // release their own claim). Removes the need for
                // the separate trailing action chip the sheet used
                // to carry.
                const nameActionKind: "claim" | "bump" | "release" | null =
                  inEdit ? null
                  : elig.kind === "claim" ? "claim"
                  : elig.kind === "bump" ? "bump"
                  : elig.kind === "own" ? "release"
                  : null;
                const nameSlotHint =
                  nameActionKind === "claim" ? "Click to claim this row"
                  : nameActionKind === "bump" ? "Bump the current claimant — you have more seniority"
                  : nameActionKind === "release" ? "Release this row"
                  : undefined;
                const nameSlotClick = nameActionKind == null ? undefined : () => {
                  if (claimPending) return;
                  if (nameActionKind === "release") onRelease(r.id);
                  else onClaim(r.id);
                };
                const nameSlotBaseCls =
                  "w-full text-left truncate block "
                  + (nameActionKind === "claim"
                      ? "hover:text-sky-800 "
                      : nameActionKind === "bump"
                        ? "hover:text-amber-800 "
                        : nameActionKind === "release"
                          ? "hover:text-rose-700 "
                          : "");
                const NameSlotInner = claimant ? (
                  <span className={claimedByMe ? "font-semibold text-sky-800" : "text-slate-800"}>
                    {claimant.lastName}, {claimant.firstName}
                    {claimant.hireDate && (
                      <span className="ml-1 text-[10px] text-slate-400">
                        · {claimant.hireDate}
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="text-slate-400 italic">— open —</span>
                );
                return (
                  <tr key={r.id} className={"border-b border-slate-100 hover:bg-slate-50 " + rowBg}>
                    <td className={"px-2 py-0.5 sticky left-0 truncate " + (rowBg || "bg-white")}>
                      {nameActionKind ? (
                        <button
                          type="button"
                          onClick={nameSlotClick}
                          disabled={claimPending}
                          className={nameSlotBaseCls + "disabled:opacity-50"}
                          title={nameSlotHint}
                        >
                          {NameSlotInner}
                        </button>
                      ) : (
                        <span className={nameSlotBaseCls}>{NameSlotInner}</span>
                      )}
                    </td>
                    {days.map((d) => {
                      const date = ymd(d);
                      const isOff = off.has(date);
                      const weekend = d.getDay() === 0 || d.getDay() === 6;
                      return (
                        <td
                          key={date}
                          onClick={inEdit ? () => onToggleOff(r.id, date, r.offDates) : undefined}
                          className={
                            "text-center py-0.5 tabular-nums font-semibold "
                            + (weekend ? "bg-slate-50/60 " : "")
                            + (isOff ? "text-slate-500 " : "text-transparent ")
                            + (inEdit ? "cursor-pointer hover:bg-amber-100" : "")
                          }
                        >
                          {isOff ? "XXX" : "·"}
                        </td>
                      );
                    })}
                    <td className="px-2 py-0.5 text-right whitespace-nowrap">
                      {inEdit && !locked ? (
                        <button
                          type="button"
                          onClick={() => onRemoveRow(r.id)}
                          className="text-slate-400 hover:text-rose-600 text-sm leading-none px-1"
                          title="Remove this row"
                          aria-label="Remove row"
                        >×</button>
                      ) : elig.kind === "held" ? (
                        <span
                          className="text-[10px] uppercase tracking-wider text-slate-400"
                          title="Held by an officer of equal or greater seniority"
                        >
                          Held
                        </span>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
              {inEdit && (
                <tr>
                  <td
                    colSpan={1 + days.length + 1}
                    className="px-2 py-1.5 text-center"
                  >
                    <button
                      type="button"
                      onClick={onAddRow}
                      className="text-xs font-medium text-sky-700 hover:text-sky-900 hover:underline"
                    >
                      + Add row
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {canEdit && rows.length > 0 && unclaimedOfficers.length > 0 && (
          <footer className="px-3 py-2 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-2 text-xs text-slate-600">
            <span>
              <span className="font-semibold">{unclaimedOfficers.length}</span>{" "}
              eligible officer{unclaimedOfficers.length === 1 ? "" : "s"} haven't picked yet
            </span>
            <button
              type="button"
              onClick={onNotifyUnclaimed}
              className="text-[11px] font-semibold uppercase tracking-wider text-sky-700 hover:underline"
              title="Send a push + email + inbox notification to each officer who hasn't claimed a row"
            >
              Notify unclaimed →
            </button>
          </footer>
        )}
      </section>
    </div>
  );
}
